import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  Steps,
  Button,
  Card,
  message,
  Input,
  Select,
  Switch,
  Slider,
  DatePicker,
  InputNumber,
  Divider,
  Tag,
  Row,
  Col,
  Modal,
  Checkbox,
  TimePicker,
  Tooltip,
  Spin,
  Badge,
  Typography,
  Space,
  Alert,
} from "antd";
import {
  createCampaign,
  updateCampaign,
  addRecipients,
  setWizardStep,
  setWizardDraft,
  clearWizardDraft,
  fetchSegments,
  fetchPlaybooks,
  fetchCampaign,
  checkSpamScore,
  fetchWarmUpProjection,
  changeCampaignStatus,
} from "../../../Slices/bulkOutreachSlice";

// ── Sub-component imports (graceful fallback if not yet created) ────────────
let RecipientSelector = null;
let StepBuilder = null;
let VerificationPanel = null;
let SpamScoreCard = null;
let WarmUpPreview = null;
let ComplianceConfig = null;

try { RecipientSelector = require("./components/RecipientSelector").default; } catch (e) { /* will use fallback */ }
try { StepBuilder = require("./components/StepBuilder").default; } catch (e) { /* will use fallback */ }
try { VerificationPanel = require("./components/VerificationPanel").default; } catch (e) { /* will use fallback */ }
try { SpamScoreCard = require("./components/SpamScoreCard").default; } catch (e) { /* will use fallback */ }
try { WarmUpPreview = require("./components/WarmUpPreview").default; } catch (e) { /* will use fallback */ }
try { ComplianceConfig = require("./components/ComplianceConfig").default; } catch (e) { /* will use fallback */ }

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

// ── Constants ───────────────────────────────────────────────────────────────

const STEP_LABELS = [
  "Campaign Info",
  "Recipients",
  "Build Steps",
  "Sending Config",
  "Warm-up Config",
  "Compliance",
  "Verification",
  "Review & Launch",
];

const STEP_DESCRIPTIONS = [
  "Name, description & sender setup",
  "Select target contacts",
  "Design your email sequence",
  "Drip & scheduling settings",
  "Warm-up volume ramp",
  "Legal & consent settings",
  "Pre-flight checks",
  "Final review & launch",
];

const WEEKDAYS = [
  { label: "Mon", value: "monday" },
  { label: "Tue", value: "tuesday" },
  { label: "Wed", value: "wednesday" },
  { label: "Thu", value: "thursday" },
  { label: "Fri", value: "friday" },
  { label: "Sat", value: "saturday" },
  { label: "Sun", value: "sunday" },
];

const DEFAULT_DRIP_CONFIG = {
  batchSize: 50,
  intervalMinutes: 60,
  maxPerHour: 100,
  dailyLimit: 200,
  sendWindowStart: "09:00",
  sendWindowEnd: "17:00",
  sendWindows: [{ start: "09:00", end: "17:00" }],
  weekdays: ["monday", "tuesday", "wednesday", "thursday", "friday"],
  timezoneAware: true,
  useBestTime: false,
};

const DEFAULT_WARMUP_CONFIG = {
  enabled: false,
  startVolume: 10,
  maxVolume: 200,
  rampFactor: 1.3,
  bounceThreshold: 5,
};

const DEFAULT_COMPLIANCE_CONFIG = {
  includePhysicalAddress: true,
  physicalAddress: "BluePlanIt Corporation, 2108 N. Street, Suite 10211, Sacramento, CA 95816",
  cooldownDays: 7,
  consentRequired: true,
  includeUnsubscribeLink: true,
};

const AVAILABLE_VARIABLES = [
  "{{firstName}}",
  "{{lastName}}",
  "{{email}}",
  "{{company}}",
  "{{title}}",
  "{{phone}}",
  "{{city}}",
  "{{state}}",
  "{{customField1}}",
  "{{customField2}}",
];

// ── Styles ──────────────────────────────────────────────────────────────────

const styles = {
  wizardContainer: {
    padding: "24px",
    minHeight: "calc(100vh - 120px)",
    background: "linear-gradient(135deg, #f0f2ff 0%, #e8f4f8 50%, #f5f0ff 100%)",
  },
  stepsCard: {
    background: "rgba(255, 255, 255, 0.72)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    borderRadius: "16px",
    border: "1px solid rgba(255, 255, 255, 0.6)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.06)",
    padding: "24px 32px",
    marginBottom: "24px",
  },
  contentCard: {
    background: "rgba(255, 255, 255, 0.78)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    borderRadius: "16px",
    border: "1px solid rgba(255, 255, 255, 0.65)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
    padding: "32px",
    minHeight: "460px",
  },
  footerBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "24px",
    padding: "16px 32px",
    background: "rgba(255, 255, 255, 0.72)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    borderRadius: "16px",
    border: "1px solid rgba(255, 255, 255, 0.6)",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  },
  glassInput: {
    borderRadius: "10px",
    border: "1px solid rgba(200, 200, 220, 0.5)",
    background: "rgba(255, 255, 255, 0.5)",
    backdropFilter: "blur(6px)",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#1a1a2e",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  summaryCard: {
    background: "linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)",
    borderRadius: "12px",
    border: "1px solid rgba(99, 102, 241, 0.15)",
    padding: "16px 20px",
    textAlign: "center",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  summaryValue: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#4f46e5",
    lineHeight: 1.2,
  },
  summaryLabel: {
    fontSize: "12px",
    fontWeight: 500,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginTop: "4px",
  },
  tagChip: {
    borderRadius: "16px",
    padding: "2px 12px",
    border: "1px solid rgba(99, 102, 241, 0.3)",
    background: "rgba(99, 102, 241, 0.08)",
    color: "#4f46e5",
    fontSize: "12px",
  },
  primaryBtn: {
    borderRadius: "10px",
    height: "42px",
    fontWeight: 600,
    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
    border: "none",
    boxShadow: "0 4px 14px rgba(99, 102, 241, 0.35)",
    minWidth: "120px",
  },
  secondaryBtn: {
    borderRadius: "10px",
    height: "42px",
    fontWeight: 600,
    border: "1px solid rgba(99, 102, 241, 0.3)",
    color: "#4f46e5",
    minWidth: "120px",
  },
  launchBtn: {
    borderRadius: "12px",
    height: "48px",
    fontWeight: 700,
    background: "linear-gradient(135deg, #10b981, #059669)",
    border: "none",
    boxShadow: "0 4px 14px rgba(16, 185, 129, 0.35)",
    fontSize: "16px",
    minWidth: "180px",
  },
  scheduleBtn: {
    borderRadius: "12px",
    height: "48px",
    fontWeight: 700,
    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
    border: "none",
    boxShadow: "0 4px 14px rgba(99, 102, 241, 0.35)",
    fontSize: "16px",
    minWidth: "180px",
  },
  reviewSection: {
    background: "rgba(255, 255, 255, 0.5)",
    borderRadius: "12px",
    border: "1px solid rgba(200, 200, 220, 0.35)",
    padding: "20px",
    marginBottom: "16px",
  },
  reviewLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "4px",
  },
  reviewValue: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#1a1a2e",
  },
  stepItemCard: {
    background: "rgba(255, 255, 255, 0.55)",
    borderRadius: "12px",
    border: "1px solid rgba(200, 200, 220, 0.35)",
    padding: "16px",
    marginBottom: "12px",
    transition: "box-shadow 0.2s",
  },
};

// ── Fallback sub-component wrappers ─────────────────────────────────────────

const FallbackPlaceholder = ({ name, children }) => (
  <div style={{ padding: "20px", textAlign: "center", color: "#9ca3af" }}>
    <Text type="secondary" style={{ fontSize: "14px" }}>
      {name} component will be rendered here once created.
    </Text>
    {children}
  </div>
);

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════

const CampaignWizard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── Redux selectors ─────────────────────────────────────────────────────
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const {
    wizardStep,
    wizardDraft,
    currentCampaign,
    playbooks,
    segments,
    spamScore,
    warmUpProjection,
    verificationResults,
    loading,
    error,
  } = useSelector((state) => state.bulkOutreach);

  // ── Local state ─────────────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(wizardStep || 0);
  // Track the highest step the user has reached — allows clicking any step up to this
  const [highestStepReached, setHighestStepReached] = useState(wizardStep || 0);
  const [campaignId, _setCampaignId] = useState(wizardDraft?.campaignId || null);
  const campaignIdRef = useRef(campaignId);
  const setCampaignId = (id) => { _setCampaignId(id); campaignIdRef.current = id; };
  const [saving, setSaving] = useState(false);
  const [launching, setLaunching] = useState(false);

  // Step 0: Campaign Info
  const [campaignName, setCampaignName] = useState(wizardDraft?.name || "");
  const [campaignDescription, setCampaignDescription] = useState(wizardDraft?.description || "");
  const [campaignTags, setCampaignTags] = useState(wizardDraft?.tags || []);
  const [fromEmail, setFromEmail] = useState(wizardDraft?.fromEmail || "");
  const replyTo = fromEmail; // Reply-To always matches From Email
  const [selectedPlaybook, setSelectedPlaybook] = useState(null);
  const [playbookModalVisible, setPlaybookModalVisible] = useState(false);

  // Step 1: Recipients
  const [selectedContacts, setSelectedContacts] = useState(wizardDraft?.selectedContacts || []);
  const [recipientFilters, setRecipientFilters] = useState(wizardDraft?.recipientFilters || null);

  // Step 6: Verification — saved preflight check statuses
  const [savedPreflightChecks, setSavedPreflightChecks] = useState(null);

  // Step 2: Build Steps
  const [sequenceSteps, setSequenceSteps] = useState(
    wizardDraft?.sequenceSteps || [
      {
        stepNumber: 1,
        subject: "",
        templateId: null,
        templateName: "",
        delayDays: 0,
        delayHours: 0,
        targeting: "all",
        abTest: null,
        autoFollowUp: false,
      },
    ]
  );

  // Step 3: Sending Config
  const [dripConfig, setDripConfig] = useState(wizardDraft?.dripConfig || { ...DEFAULT_DRIP_CONFIG });

  // Step 4: Warm-up Config
  const [warmupConfig, setWarmupConfig] = useState(wizardDraft?.warmupConfig || { ...DEFAULT_WARMUP_CONFIG });

  // Step 5: Compliance
  const [complianceConfig, setComplianceConfig] = useState(
    wizardDraft?.complianceConfig || { ...DEFAULT_COMPLIANCE_CONFIG }
  );

  // Step 7: Review & Launch
  const [launchMode, setLaunchMode] = useState("now"); // "now" | "scheduled"
  const [scheduledAt, setScheduledAt] = useState(null);
  const [saveAsPlaybook, setSaveAsPlaybook] = useState(false);
  const [playbookName, setPlaybookName] = useState("");
  const [confirmLaunchVisible, setConfirmLaunchVisible] = useState(false);

  // Track if draft has been persisted at least once
  const hasSavedRef = useRef(false);
  const autoSaveTimerRef = useRef(null);

  // Refs to always have the latest state values in persistDraft (avoids stale closure)
  const complianceConfigRef = useRef(complianceConfig);
  complianceConfigRef.current = complianceConfig;
  const dripConfigRef = useRef(dripConfig);
  dripConfigRef.current = dripConfig;
  const warmupConfigRef = useRef(warmupConfig);
  warmupConfigRef.current = warmupConfig;

  // ── Derived data ────────────────────────────────────────────────────────
  const salesEmails = useMemo(() => {
    return (loginUser?.salesEmailSettings || [])
      .filter((item) => item.status === true || item.status === "true")
      .map((item) => item.email)
      .filter(Boolean);
  }, [loginUser?.salesEmailSettings]);

  const recipientStats = useMemo(() => {
    const contacts = selectedContacts || [];
    return {
      total: contacts.length,
      leads: contacts.filter((c) => c.type === "lead" || c.leadId).length,
      contacts: contacts.filter((c) => c.type === "contact" || (!c.leadId && !c.type)).length,
      optedIn: contacts.filter((c) => c.optIn === true || c.optIn === "true").length,
      verified: contacts.filter((c) => c.emailVerified === true || c.verified === true).length,
      selected: contacts.length,
    };
  }, [selectedContacts]);

  // ── Load playbooks & segments on mount ──────────────────────────────────
  useEffect(() => {
    dispatch(fetchPlaybooks({ limit: 100 }));
    dispatch(fetchSegments({ limit: 100 }));
  }, [dispatch]);

  // ── Restore draft from Redux on mount ───────────────────────────────────
  useEffect(() => {
    if (wizardDraft && !hasSavedRef.current) {
      if (wizardDraft.name) setCampaignName(wizardDraft.name);
      if (wizardDraft.description) setCampaignDescription(wizardDraft.description);
      if (wizardDraft.tags) setCampaignTags(wizardDraft.tags);
      if (wizardDraft.fromEmail) setFromEmail(wizardDraft.fromEmail);
      // replyTo always matches fromEmail — no separate state needed
      if (wizardDraft.selectedContacts) setSelectedContacts(wizardDraft.selectedContacts);
      if (wizardDraft.sequenceSteps) setSequenceSteps(wizardDraft.sequenceSteps);
      if (wizardDraft.dripConfig) setDripConfig(wizardDraft.dripConfig);
      if (wizardDraft.warmupConfig) setWarmupConfig(wizardDraft.warmupConfig);
      if (wizardDraft.complianceConfig) setComplianceConfig(wizardDraft.complianceConfig);
      if (wizardDraft.campaignId) setCampaignId(wizardDraft.campaignId);
      if (typeof wizardStep === "number") setCurrentStep(wizardStep);
    }
  }, []); // intentionally run only once on mount

  // ── Load existing campaign when editing a draft ────────────────────────
  const editId = searchParams.get("edit");
  const editLoadedRef = useRef(false);
  useEffect(() => {
    if (!editId) return;
    // Only load once per mount — prevents stale wizardDraft from blocking reload
    if (editLoadedRef.current) return;
    editLoadedRef.current = true;

    // Clear any stale wizard state before loading fresh data
    dispatch(clearWizardDraft());

    dispatch(fetchCampaign(editId)).unwrap().then((campaign) => {
      if (!campaign) return;
      // Populate all form fields from the fetched campaign
      setCampaignId(campaign._id);
      if (campaign.name) setCampaignName(campaign.name);
      if (campaign.description) setCampaignDescription(campaign.description);
      if (campaign.tags?.length) setCampaignTags(campaign.tags);
      if (campaign.fromEmail) setFromEmail(campaign.fromEmail);

      // Map backend steps → wizard sequenceSteps format
      if (campaign.steps?.length) {
        setSequenceSteps(campaign.steps.map((s, idx) => ({
          stepNumber: s.stepNumber || (idx + 1),
          subject: s.name || s.subject || (idx === 0 ? campaign.subjectLine : "") || "",
          templateId: s.templateId || null,
          templateName: s.templateName || "",
          delayDays: s.delayDays || 0,
          delayHours: s.delayHours || 0,
          targeting: s.targeting || "all",
          abTest: s.abTest?.enabled ? s.abTest : null,
          autoFollowUp: s.autoFollowUp?.enabled || false,
          autoFollowUpTrigger: s.autoFollowUp?.triggerEvent || 'opened_no_reply',
          autoFollowUpDelay: s.autoFollowUp?.triggerDelayHours || 48,
          autoFollowUpSubject: s.autoFollowUp?.followUpSubject || '',
          autoFollowUpBody: s.autoFollowUp?.followUpBody || '',
          fallbackValues: s.fallbackValues || {},
          variantA: s.abTest?.variants?.[0] ? { subject: s.abTest.variants[0].subject, body: s.abTest.variants[0].body } : { subject: '', body: '' },
          variantB: s.abTest?.variants?.[1] ? { subject: s.abTest.variants[1].subject, body: s.abTest.variants[1].body } : { subject: '', body: '' },
        })));
      } else if (campaign.subjectLine) {
        // No steps saved yet but subject line exists — put it on step 1
        setSequenceSteps([{
          stepNumber: 1,
          subject: campaign.subjectLine,
          templateId: null,
          templateName: "",
          delayDays: 0,
          delayHours: 0,
          targeting: "all",
          abTest: null,
          autoFollowUp: false,
        }]);
      }

      // Map backend drip → wizard dripConfig
      if (campaign.drip) {
        setDripConfig({
          batchSize: campaign.drip.batchSize ?? DEFAULT_DRIP_CONFIG.batchSize,
          intervalMinutes: campaign.drip.minDelaySeconds != null ? Math.round(campaign.drip.minDelaySeconds / 60) : DEFAULT_DRIP_CONFIG.intervalMinutes,
          maxPerHour: campaign.drip.maxPerHour ?? DEFAULT_DRIP_CONFIG.maxPerHour,
          dailyLimit: campaign.drip.maxPerDay ?? DEFAULT_DRIP_CONFIG.dailyLimit,
          sendWindowStart: campaign.drip.sendWindowStart || DEFAULT_DRIP_CONFIG.sendWindowStart,
          sendWindowEnd: campaign.drip.sendWindowEnd || DEFAULT_DRIP_CONFIG.sendWindowEnd,
          sendWindows: campaign.drip.sendWindows?.length
            ? campaign.drip.sendWindows
            : (campaign.drip.sendWindowStart
              ? [{ start: campaign.drip.sendWindowStart, end: campaign.drip.sendWindowEnd || '17:00' }]
              : DEFAULT_DRIP_CONFIG.sendWindows),
          weekdays: campaign.drip.sendDays?.length ? campaign.drip.sendDays : DEFAULT_DRIP_CONFIG.weekdays,
          timezoneAware: campaign.drip.timezoneAware ?? DEFAULT_DRIP_CONFIG.timezoneAware,
          useBestTime: campaign.drip.useBestTime ?? DEFAULT_DRIP_CONFIG.useBestTime,
        });
      }

      // Map backend warmUp → wizard warmupConfig
      if (campaign.warmUp) {
        setWarmupConfig({
          enabled: campaign.warmUp.enabled || false,
          startVolume: campaign.warmUp.startVolume ?? DEFAULT_WARMUP_CONFIG.startVolume,
          maxVolume: campaign.warmUp.maxVolume ?? DEFAULT_WARMUP_CONFIG.maxVolume,
          rampFactor: campaign.warmUp.rampFactor ?? DEFAULT_WARMUP_CONFIG.rampFactor,
          bounceThreshold: campaign.warmUp.bounceThreshold ?? DEFAULT_WARMUP_CONFIG.bounceThreshold,
        });
      }

      // Map backend compliance
      if (campaign.compliance) {
        setComplianceConfig({
          includePhysicalAddress: campaign.compliance.includePhysicalAddress ?? DEFAULT_COMPLIANCE_CONFIG.includePhysicalAddress,
          physicalAddress: campaign.compliance.physicalAddress || DEFAULT_COMPLIANCE_CONFIG.physicalAddress,
          cooldownDays: campaign.cooldown?.minDaysBetween ?? DEFAULT_COMPLIANCE_CONFIG.cooldownDays,
          consentRequired: campaign.compliance.consentRequired ?? DEFAULT_COMPLIANCE_CONFIG.consentRequired,
          includeUnsubscribeLink: campaign.compliance.includeUnsubscribeLink ?? DEFAULT_COMPLIANCE_CONFIG.includeUnsubscribeLink,
        });
      }

      // Map recipients if present
      if (campaign.recipients?.length) {
        setSelectedContacts(campaign.recipients.map((r) => ({
          leadId: r.leadId,
          email: r.contactEmail,
          contactName: r.contactName,
          agencyName: r.agencyName,
          timeZone: r.timeZone,
          contactTitle: r.contactTitle,
          type: "lead",
        })));
      }

      // Restore recipient filters if saved
      if (campaign.recipientFilter) {
        setRecipientFilters(campaign.recipientFilter);
      }

      // Restore preflight check statuses so verification panel shows completed checks
      if (campaign.preflightChecks?.stepStatuses?.length) {
        setSavedPreflightChecks(campaign.preflightChecks);
      }

      // Restore scheduled send time
      if (campaign.scheduledAt) {
        setScheduledAt(dayjs(campaign.scheduledAt));
        setLaunchMode("scheduled");
      }

      // Restore wizard step position so user resumes where they left off
      if (typeof campaign.lastWizardStep === 'number' && campaign.lastWizardStep > 0) {
        setCurrentStep(campaign.lastWizardStep);
        setHighestStepReached(campaign.lastWizardStep);
        dispatch(setWizardStep(campaign.lastWizardStep));
      }

      // Sync to Redux wizardDraft
      dispatch(setWizardDraft({ campaignId: campaign._id, name: campaign.name }));
      hasSavedRef.current = true;
    }).catch((err) => {
      message.error(typeof err === "string" ? err : "Failed to load draft campaign");
    });
  }, [editId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Set default fromEmail when salesEmails load ─────────────────────────
  useEffect(() => {
    if (!fromEmail && salesEmails.length > 0) {
      setFromEmail(salesEmails[0]);
    }
  }, [salesEmails, fromEmail]);

  // ── Cleanup on unmount ──────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, []);

  // ── Build draft payload ─────────────────────────────────────────────────
  const buildDraftPayload = useCallback(() => {
    return {
      name: campaignName,
      description: campaignDescription,
      tags: campaignTags,
      fromEmail,
      replyTo,
      selectedContacts,
      sequenceSteps,
      dripConfig,
      warmupConfig,
      complianceConfig,
      recipientFilters,
      campaignId,
    };
  }, [
    campaignName, campaignDescription, campaignTags, fromEmail, replyTo,
    selectedContacts, sequenceSteps, dripConfig, warmupConfig, complianceConfig, recipientFilters, campaignId,
  ]);

  // ── Save draft to Redux (local state) ───────────────────────────────────
  const saveDraftToRedux = useCallback(() => {
    const draft = buildDraftPayload();
    dispatch(setWizardDraft(draft));
  }, [dispatch, buildDraftPayload]);

  // ── Persist draft to backend ────────────────────────────────────────────
  const persistDraft = useCallback(async () => {
    if (!campaignName.trim()) return; // need at least a name to persist
    setSaving(true);
    try {
      const payload = {
        name: campaignName.trim(),
        description: campaignDescription,
        tags: campaignTags,
        fromEmail,
        replyToEmail: fromEmail,
        subjectLine: sequenceSteps[0]?.subject || '',
        status: "draft",
        steps: sequenceSteps.map((s) => ({
          stepNumber: s.stepNumber,
          name: s.subject || "",
          templateId: s.templateId || null,
          templateName: s.templateName || "",
          delayDays: s.delayDays || 0,
          delayHours: s.delayHours || 0,
          targeting: s.targeting || "all",
          abTest: s.abTest ? {
            enabled: true,
            testPercentage: s.abTest.testPercentage || 20,
            winnerCriteria: s.abTest.winnerCriteria || 'open_rate',
            winnerWaitHours: s.abTest.winnerWaitHours || 4,
            variants: s.abTest.variants || [
              { variantId: 'A', subject: s.variantA?.subject || '', body: s.variantA?.body || '' },
              { variantId: 'B', subject: s.variantB?.subject || '', body: s.variantB?.body || '' },
            ],
          } : null,
          autoFollowUp: s.autoFollowUp ? {
            enabled: true,
            triggerEvent: s.autoFollowUpTrigger || 'opened_no_reply',
            triggerDelayHours: s.autoFollowUpDelay || 48,
            followUpSubject: s.autoFollowUpSubject || '',
            followUpBody: s.autoFollowUpBody || '',
          } : { enabled: false },
          fallbackValues: s.fallbackValues || {},
        })),
        drip: (() => {
          const dc = dripConfigRef.current;
          return {
            batchSize: dc.batchSize ?? 1,
            minDelaySeconds: (dc.intervalMinutes ?? 1) * 60,
            maxDelaySeconds: ((dc.intervalMinutes ?? 1) * 60) + 60,
            maxPerHour: dc.maxPerHour ?? 100,
            maxPerDay: dc.dailyLimit ?? 200,
            sendWindowStart: dc.sendWindows?.[0]?.start || dc.sendWindowStart || '09:00',
            sendWindowEnd: dc.sendWindows?.[0]?.end || dc.sendWindowEnd || '17:00',
            sendWindows: dc.sendWindows || [],
            sendDays: dc.weekdays || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
            timezoneAware: dc.timezoneAware ?? true,
            useBestTime: dc.useBestTime ?? true,
          };
        })(),
        warmUp: warmupConfigRef.current,
        compliance: (() => {
          const cc = complianceConfigRef.current;
          return {
            includePhysicalAddress: cc.includePhysicalAddress ?? true,
            physicalAddress: cc.physicalAddress || '',
            includeUnsubscribeLink: cc.includeUnsubscribeLink ?? true,
            consentRequired: cc.consentRequired ?? false,
          };
        })(),
        cooldown: {
          enabled: true,
          minDaysBetween: complianceConfigRef.current.cooldownDays ?? 3,
        },
        recipientFilter: recipientFilters,
        lastWizardStep: Math.max(currentStep, highestStepReached),
        ...(scheduledAt ? { scheduledAt: scheduledAt.toISOString ? scheduledAt.toISOString() : scheduledAt } : {}),
      };

      let result;
      const existingId = campaignIdRef.current || campaignId;
      if (existingId) {
        result = await dispatch(updateCampaign({ id: existingId, ...payload })).unwrap();
      } else {
        result = await dispatch(createCampaign(payload)).unwrap();
        if (result?._id) {
          setCampaignId(result._id);
          dispatch(setWizardDraft({ campaignId: result._id }));
        }
      }
      hasSavedRef.current = true;
      return result;
    } catch (err) {
      message.error(typeof err === "string" ? err : "Failed to save draft");
      return null;
    } finally {
      setSaving(false);
    }
  }, [
    dispatch, campaignId, campaignName, campaignDescription, campaignTags,
    fromEmail, replyTo, sequenceSteps, dripConfig, warmupConfig, complianceConfig, recipientFilters,
    currentStep, highestStepReached, scheduledAt,
  ]);

  // ── Step validation ─────────────────────────────────────────────────────
  const validateStep = useCallback(
    (step) => {
      switch (step) {
        case 0: {
          if (!campaignName.trim()) {
            message.warning("Campaign name is required");
            return false;
          }
          if (campaignName.trim().length < 3) {
            message.warning("Campaign name must be at least 3 characters");
            return false;
          }
          if (!fromEmail) {
            message.warning("Please select a From Email address");
            return false;
          }
          return true;
        }
        case 1: {
          if (!selectedContacts || selectedContacts.length === 0) {
            message.warning("Please select at least one recipient");
            return false;
          }
          return true;
        }
        case 2: {
          if (!sequenceSteps || sequenceSteps.length === 0) {
            message.warning("Please add at least one step to your sequence");
            return false;
          }
          const hasEmptySubject = sequenceSteps.some((s) => !s.subject?.trim() && !s.templateId);
          if (hasEmptySubject) {
            message.warning("Each step needs either a subject line or a template");
            return false;
          }
          return true;
        }
        case 3: {
          if (dripConfig.batchSize < 1) {
            message.warning("Batch size must be at least 1");
            return false;
          }
          if (dripConfig.dailyLimit < dripConfig.batchSize) {
            message.warning("Daily limit must be greater than or equal to batch size");
            return false;
          }
          if (dripConfig.weekdays.length === 0) {
            message.warning("Please select at least one sending day");
            return false;
          }
          return true;
        }
        case 4: {
          if (warmupConfig.enabled) {
            if (warmupConfig.startVolume < 1) {
              message.warning("Start volume must be at least 1");
              return false;
            }
            if (warmupConfig.maxVolume <= warmupConfig.startVolume) {
              message.warning("Max volume must be greater than start volume");
              return false;
            }
            if (warmupConfig.rampFactor < 1.01) {
              message.warning("Ramp factor must be greater than 1");
              return false;
            }
          }
          return true;
        }
        case 5: {
          if (complianceConfig.consentRequired && complianceConfig.cooldownDays < 1) {
            message.warning("Cooldown days must be at least 1 when consent is required");
            return false;
          }
          return true;
        }
        case 6:
          return true; // verification is informational
        case 7:
          return true; // review step
        default:
          return true;
      }
    },
    [campaignName, fromEmail, selectedContacts, sequenceSteps, dripConfig, warmupConfig, complianceConfig]
  );

  // ── Step navigation handlers ────────────────────────────────────────────
  const handleNext = useCallback(async () => {
    if (!validateStep(currentStep)) return;

    // Save draft to Redux on every step change
    saveDraftToRedux();

    // Persist to backend on every step so draft data is never lost
    await persistDraft();

    // Add recipients after step 1 — replace existing when editing a draft
    if (currentStep === 1 && campaignId && selectedContacts.length > 0) {
      try {
        await dispatch(
          addRecipients({
            campaignId,
            replace: true,
            contacts: selectedContacts.map((c) => ({
              contactEmail: c.email || c.contactEmail || '',
              contactName: c.contactName || c.contactPerson || c.firstName || c.firstname || '',
              contactTitle: c.title || c.contactTitle || '',
              agencyName: c.agencyName || c.company || '',
              leadId: c.leadId || c._id || '',
              timeZone: c.timeZone || '',
            })),
          })
        ).unwrap();
      } catch (err) {
        message.error("Failed to save recipients: " + (typeof err === "string" ? err : "Unknown error"));
        return;
      }
    }

    const next = currentStep + 1;
    setCurrentStep(next);
    setHighestStepReached((prev) => Math.max(prev, next));
    dispatch(setWizardStep(next));
  }, [currentStep, validateStep, saveDraftToRedux, persistDraft, dispatch, campaignId, selectedContacts]);

  const handleBack = useCallback(() => {
    saveDraftToRedux();
    const prev = Math.max(0, currentStep - 1);
    setCurrentStep(prev);
    dispatch(setWizardStep(prev));
  }, [currentStep, saveDraftToRedux, dispatch]);

  const handleStepClick = useCallback(
    (step) => {
      // Allow clicking any step up to the highest visited step
      if (step <= highestStepReached) {
        saveDraftToRedux();
        setCurrentStep(step);
        dispatch(setWizardStep(step));
      }
    },
    [highestStepReached, saveDraftToRedux, dispatch]
  );

  // ── Load from Playbook ──────────────────────────────────────────────────
  const handleLoadPlaybook = useCallback(
    (playbook) => {
      if (!playbook) return;
      if (playbook.steps?.length > 0) {
        setSequenceSteps(
          playbook.steps.map((s, idx) => ({
            stepNumber: idx + 1,
            subject: s.subject || "",
            templateId: s.templateId || null,
            templateName: s.templateName || "",
            delayDays: s.delayDays || 0,
            delayHours: s.delayHours || 0,
            targeting: s.targeting || "all",
            abTest: s.abTest || null,
            autoFollowUp: s.autoFollowUp || false,
          }))
        );
      }
      if (playbook.dripConfig) setDripConfig({ ...DEFAULT_DRIP_CONFIG, ...playbook.dripConfig });
      if (playbook.warmupConfig) setWarmupConfig({ ...DEFAULT_WARMUP_CONFIG, ...playbook.warmupConfig });
      if (playbook.complianceConfig) setComplianceConfig({ ...DEFAULT_COMPLIANCE_CONFIG, ...playbook.complianceConfig });
      if (playbook.tags?.length > 0) setCampaignTags(playbook.tags);

      message.success(`Loaded playbook: ${playbook.name}`);
      setPlaybookModalVisible(false);
      setSelectedPlaybook(null);
    },
    []
  );

  // ── Contacts change handler ─────────────────────────────────────────────
  const handleContactsChange = useCallback((contacts) => {
    setSelectedContacts(contacts || []);
  }, []);

  // ── Sequence step management ────────────────────────────────────────────
  const handleAddStep = useCallback(() => {
    const nextNum = sequenceSteps.length + 1;
    setSequenceSteps((prev) => [
      ...prev,
      {
        stepNumber: nextNum,
        subject: "",
        templateId: null,
        templateName: "",
        delayDays: nextNum === 1 ? 0 : 3,
        delayHours: 0,
        targeting: "all",
        abTest: null,
        autoFollowUp: false,
      },
    ]);
  }, [sequenceSteps.length]);

  const handleRemoveStep = useCallback((index) => {
    setSequenceSteps((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      return updated.map((s, i) => ({ ...s, stepNumber: i + 1 }));
    });
  }, []);

  const handleUpdateStep = useCallback((index, field, value) => {
    setSequenceSteps((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  // ── Drip config handler ─────────────────────────────────────────────────
  const handleDripChange = useCallback((field, value) => {
    setDripConfig((prev) => ({ ...prev, [field]: value }));
  }, []);

  // ── Warm-up config handler ──────────────────────────────────────────────
  const handleWarmupChange = useCallback((field, value) => {
    setWarmupConfig((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Fetch warm-up projection when config changes
  useEffect(() => {
    if (warmupConfig.enabled && warmupConfig.startVolume > 0 && warmupConfig.maxVolume > 0) {
      const timer = setTimeout(() => {
        dispatch(
          fetchWarmUpProjection({
            startVolume: warmupConfig.startVolume,
            maxVolume: warmupConfig.maxVolume,
            rampFactor: warmupConfig.rampFactor,
            days: 30,
          })
        );
      }, 500); // debounce
      return () => clearTimeout(timer);
    }
  }, [dispatch, warmupConfig.enabled, warmupConfig.startVolume, warmupConfig.maxVolume, warmupConfig.rampFactor]);

  // ── Compliance config handler ───────────────────────────────────────────
  const handleComplianceChange = useCallback((field, value) => {
    setComplianceConfig((prev) => ({ ...prev, [field]: value }));
  }, []);

  // ── Launch / Schedule handlers ──────────────────────────────────────────
  const handleLaunch = useCallback(async () => {
    if (!campaignId) {
      message.error("Campaign must be saved before launching. Please go back and try again.");
      return;
    }

    setLaunching(true);
    try {
      // Final save
      await persistDraft();

      const statusPayload = {
        id: campaignId,
        status: launchMode === "now" ? "active" : "scheduled",
      };
      if (launchMode === "scheduled" && scheduledAt) {
        statusPayload.scheduledAt = scheduledAt.toISOString();
      }

      await dispatch(changeCampaignStatus(statusPayload)).unwrap();

      // Save as playbook if requested
      if (saveAsPlaybook && playbookName.trim()) {
        try {
          const { createPlaybook } = require("../../../Slices/bulkOutreachSlice");
          await dispatch(
            createPlaybook({
              name: playbookName.trim(),
              steps: sequenceSteps,
              dripConfig,
              warmupConfig,
              complianceConfig,
              tags: campaignTags,
            })
          ).unwrap();
          message.success("Playbook saved successfully");
        } catch (pbErr) {
          message.warning("Campaign launched but playbook save failed");
        }
      }

      dispatch(clearWizardDraft());
      message.success(launchMode === "now" ? "Campaign launched successfully!" : "Campaign scheduled successfully!");
      setConfirmLaunchVisible(false);
      navigate("/bulk-outreach");
    } catch (err) {
      message.error(typeof err === "string" ? err : "Failed to launch campaign");
    } finally {
      setLaunching(false);
    }
  }, [
    dispatch, navigate, campaignId, launchMode, scheduledAt, persistDraft,
    saveAsPlaybook, playbookName, sequenceSteps, dripConfig, warmupConfig,
    complianceConfig, campaignTags,
  ]);

  const handleSaveDraftAndExit = useCallback(async () => {
    saveDraftToRedux();
    await persistDraft();
    message.success("Draft saved");
    navigate("/bulk-outreach");
  }, [saveDraftToRedux, persistDraft, navigate]);

  const handleDiscard = useCallback(() => {
    const campaignStatus = currentCampaign?.status;
    if (campaignStatus && !['draft'].includes(campaignStatus)) {
      message.warning('Cannot discard — this campaign is already ' + campaignStatus);
      return;
    }
    Modal.confirm({
      title: "Discard Draft?",
      content: "This will clear your unsaved draft. The campaign will remain in your drafts if it was already saved.",
      okText: "Discard Draft",
      okType: "danger",
      cancelText: "Keep Editing",
      onOk: () => {
        dispatch(clearWizardDraft());
        navigate("/bulk-outreach");
      },
    });
  }, [dispatch, navigate, currentCampaign]);

  // ═════════════════════════════════════════════════════════════════════════
  // STEP RENDERERS
  // ═════════════════════════════════════════════════════════════════════════

  // ── Step 0: Campaign Info ───────────────────────────────────────────────
  const renderCampaignInfo = () => (
    <div>
      <div style={styles.sectionTitle}>
        <span style={{ fontSize: "20px" }}>Campaign Details</span>
      </div>

      <Row gutter={[24, 20]}>
        <Col xs={24} md={16}>
          <label style={{ fontWeight: 600, color: "#374151", fontSize: "13px", marginBottom: "6px", display: "block" }}>
            Campaign Name <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <Input
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            placeholder="e.g., Q2 2026 SaaS Decision Makers Outreach"
            style={{ ...styles.glassInput, height: "42px", fontSize: "14px" }}
            maxLength={120}
          />
          <Text type="secondary" style={{ fontSize: "11px", marginTop: "4px", display: "block" }}>
            {campaignName.length}/120 characters
          </Text>
        </Col>

        <Col xs={24} md={8}>
          <label style={{ fontWeight: 600, color: "#374151", fontSize: "13px", marginBottom: "6px", display: "block" }}>
            Tags
          </label>
          <Select
            mode="tags"
            value={campaignTags}
            onChange={setCampaignTags}
            placeholder="Add tags..."
            style={{ width: "100%" }}
            dropdownStyle={{ borderRadius: "10px" }}
            tokenSeparators={[","]}
            tagRender={({ label, closable, onClose }) => (
              <Tag closable={closable} onClose={onClose} style={styles.tagChip}>
                {label}
              </Tag>
            )}
          />
        </Col>

        <Col xs={24}>
          <label style={{ fontWeight: 600, color: "#374151", fontSize: "13px", marginBottom: "6px", display: "block" }}>
            Description
          </label>
          <TextArea
            value={campaignDescription}
            onChange={(e) => setCampaignDescription(e.target.value)}
            placeholder="What is the goal of this campaign? Who is the target audience?"
            rows={3}
            style={{ ...styles.glassInput, resize: "vertical" }}
            maxLength={500}
          />
        </Col>
      </Row>

      <Divider style={{ borderColor: "rgba(200, 200, 220, 0.35)" }} />

      <div style={styles.sectionTitle}>
        <span style={{ fontSize: "20px" }}>Sender Configuration</span>
      </div>

      <Row gutter={[24, 20]}>
        <Col xs={24} md={12}>
          <label style={{ fontWeight: 600, color: "#374151", fontSize: "13px", marginBottom: "6px", display: "block" }}>
            From Email <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <Select
            value={fromEmail || undefined}
            onChange={setFromEmail}
            placeholder="Select sender email"
            style={{ width: "100%" }}
            dropdownStyle={{ borderRadius: "10px" }}
            notFoundContent={
              <Text type="secondary" style={{ padding: "8px" }}>
                No sales emails configured. Go to Settings to add one.
              </Text>
            }
          >
            {salesEmails.map((email) => (
              <Option key={email} value={email}>
                {email}
              </Option>
            ))}
          </Select>
        </Col>

        {/* Reply-To always matches From Email — no separate field needed */}
      </Row>

      <Divider style={{ borderColor: "rgba(200, 200, 220, 0.35)" }} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={styles.sectionTitle}>
          <span style={{ fontSize: "20px" }}>Load from Playbook</span>
        </div>
        <Button
          type="default"
          style={styles.secondaryBtn}
          onClick={() => setPlaybookModalVisible(true)}
        >
          Browse Playbooks
        </Button>
      </div>
      <Text type="secondary" style={{ fontSize: "13px" }}>
        Pre-fill steps, drip settings, and compliance from a saved playbook template.
      </Text>

      {/* Playbook Selection Modal */}
      <Modal
        title={<span style={{ fontWeight: 600 }}>Select a Playbook</span>}
        open={playbookModalVisible}
        onCancel={() => { setPlaybookModalVisible(false); setSelectedPlaybook(null); }}
        onOk={() => handleLoadPlaybook(selectedPlaybook)}
        okText="Load Playbook"
        okButtonProps={{ disabled: !selectedPlaybook, style: styles.primaryBtn }}
        cancelButtonProps={{ style: styles.secondaryBtn }}
        width={640}
        styles={{ body: { maxHeight: "400px", overflowY: "auto" } }}
      >
        {playbooks.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>
            <Text type="secondary">No playbooks saved yet. You can save this campaign as a playbook after launch.</Text>
          </div>
        ) : (
          <div>
            {playbooks.map((pb) => (
              <div
                key={pb._id}
                onClick={() => setSelectedPlaybook(pb)}
                style={{
                  ...styles.stepItemCard,
                  cursor: "pointer",
                  borderColor: selectedPlaybook?._id === pb._id ? "#6366f1" : "rgba(200, 200, 220, 0.35)",
                  background: selectedPlaybook?._id === pb._id
                    ? "rgba(99, 102, 241, 0.06)"
                    : "rgba(255, 255, 255, 0.55)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <Text strong style={{ fontSize: "14px" }}>{pb.name}</Text>
                    <div style={{ marginTop: "4px" }}>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        {pb.steps?.length || 0} steps
                        {pb.tags?.length > 0 && ` | ${pb.tags.join(", ")}`}
                      </Text>
                    </div>
                  </div>
                  {selectedPlaybook?._id === pb._id && (
                    <Badge
                      count="Selected"
                      style={{ backgroundColor: "#6366f1", borderRadius: "12px", fontSize: "11px" }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );

  // ── Step 1: Recipients ──────────────────────────────────────────────────
  const renderRecipients = () => (
    <div>
      <div style={styles.sectionTitle}>
        <span style={{ fontSize: "20px" }}>Select Recipients</span>
      </div>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
        {[
          { label: "Leads", value: recipientStats.leads, color: "#6366f1" },
          { label: "Contacts", value: recipientStats.contacts, color: "#8b5cf6" },
          { label: "Opted In", value: recipientStats.optedIn, color: "#10b981" },
          { label: "Verified", value: recipientStats.verified, color: "#0ea5e9" },
          { label: "Selected", value: recipientStats.selected, color: "#f59e0b" },
        ].map((stat) => (
          <Col xs={12} sm={8} md={4} lg={4} key={stat.label}>
            <div style={styles.summaryCard}>
              <div style={{ ...styles.summaryValue, color: stat.color }}>{stat.value}</div>
              <div style={styles.summaryLabel}>{stat.label}</div>
            </div>
          </Col>
        ))}
      </Row>

      {/* Recipient Selector Sub-component */}
      {RecipientSelector ? (
        <RecipientSelector
          selectedContacts={selectedContacts}
          onContactsChange={handleContactsChange}
          segments={segments}
          initialFilters={recipientFilters}
          onFiltersChange={setRecipientFilters}
        />
      ) : (
        <div style={styles.reviewSection}>
          <FallbackPlaceholder name="RecipientSelector">
            <div style={{ marginTop: "16px" }}>
              <Text type="secondary">
                You can manually add contacts below while the RecipientSelector component is being built.
              </Text>
              <div style={{ marginTop: "12px" }}>
                <Text style={{ fontSize: "13px", color: "#6b7280" }}>
                  Currently selected: <strong>{selectedContacts.length}</strong> contacts
                </Text>
              </div>
            </div>
          </FallbackPlaceholder>
        </div>
      )}
    </div>
  );

  // ── Step 2: Build Steps ─────────────────────────────────────────────────
  const renderBuildSteps = () => (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div style={styles.sectionTitle}>
          <span style={{ fontSize: "20px" }}>Email Sequence</span>
        </div>
        <Button type="primary" style={styles.primaryBtn} onClick={handleAddStep}>
          + Add Step
        </Button>
      </div>

      {StepBuilder ? (
        <StepBuilder
          steps={sequenceSteps}
          onStepsChange={setSequenceSteps}
          onAddStep={handleAddStep}
          onRemoveStep={handleRemoveStep}
          onUpdateStep={handleUpdateStep}
          availableVariables={AVAILABLE_VARIABLES}
          subjectLine={sequenceSteps[0]?.subject || ''}
          onSubjectChange={(val) => handleUpdateStep(0, 'subject', val)}
        />
      ) : (
        <div>
          {sequenceSteps.map((step, index) => (
            <div key={index} style={styles.stepItemCard}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "14px",
                    }}
                  >
                    {step.stepNumber}
                  </div>
                  <Text strong style={{ fontSize: "14px" }}>
                    Step {step.stepNumber}
                    {index === 0 ? " (Initial Email)" : ` (Follow-up, +${step.delayDays}d ${step.delayHours}h)`}
                  </Text>
                </div>
                {sequenceSteps.length > 1 && (
                  <Button
                    type="text"
                    danger
                    size="small"
                    onClick={() => handleRemoveStep(index)}
                  >
                    Remove
                  </Button>
                )}
              </div>

              <Row gutter={[16, 12]}>
                <Col xs={24}>
                  <label style={{ fontWeight: 600, color: "#374151", fontSize: "12px", marginBottom: "4px", display: "block" }}>
                    Subject Line
                  </label>
                  <Input
                    value={step.subject}
                    onChange={(e) => handleUpdateStep(index, "subject", e.target.value)}
                    placeholder="e.g., Quick question about {{company}}'s growth strategy"
                    style={{ ...styles.glassInput, height: "38px" }}
                    suffix={
                      <Tooltip
                        title={
                          <div>
                            <div style={{ fontWeight: 600, marginBottom: "4px" }}>Available variables:</div>
                            {AVAILABLE_VARIABLES.map((v) => (
                              <Tag
                                key={v}
                                style={{ cursor: "pointer", margin: "2px", fontSize: "11px" }}
                                onClick={() => {
                                  const currentSubject = step.subject || "";
                                  handleUpdateStep(index, "subject", currentSubject + " " + v);
                                }}
                              >
                                {v}
                              </Tag>
                            ))}
                          </div>
                        }
                        overlayStyle={{ maxWidth: "320px" }}
                        trigger="click"
                      >
                        <span
                          style={{
                            color: "#6366f1",
                            cursor: "pointer",
                            fontWeight: 600,
                            fontSize: "12px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {"{{"} Variables {"}}"}
                        </span>
                      </Tooltip>
                    }
                  />
                </Col>

                <Col xs={24} md={12}>
                  <label style={{ fontWeight: 600, color: "#374151", fontSize: "12px", marginBottom: "4px", display: "block" }}>
                    Template (Body)
                  </label>
                  <Input
                    value={step.templateName}
                    onChange={(e) => handleUpdateStep(index, "templateName", e.target.value)}
                    placeholder="Enter or select a template name"
                    style={{ ...styles.glassInput, height: "38px" }}
                  />
                </Col>

                {index > 0 && (
                  <>
                    <Col xs={12} md={6}>
                      <label style={{ fontWeight: 600, color: "#374151", fontSize: "12px", marginBottom: "4px", display: "block" }}>
                        Delay (Days)
                      </label>
                      <InputNumber
                        value={step.delayDays}
                        onChange={(val) => handleUpdateStep(index, "delayDays", val || 0)}
                        min={0}
                        max={90}
                        style={{ width: "100%", ...styles.glassInput }}
                      />
                    </Col>
                    <Col xs={12} md={6}>
                      <label style={{ fontWeight: 600, color: "#374151", fontSize: "12px", marginBottom: "4px", display: "block" }}>
                        Delay (Hours)
                      </label>
                      <InputNumber
                        value={step.delayHours}
                        onChange={(val) => handleUpdateStep(index, "delayHours", val || 0)}
                        min={0}
                        max={23}
                        style={{ width: "100%", ...styles.glassInput }}
                      />
                    </Col>
                  </>
                )}

                <Col xs={24} md={index > 0 ? 12 : 12}>
                  <label style={{ fontWeight: 600, color: "#374151", fontSize: "12px", marginBottom: "4px", display: "block" }}>
                    Targeting
                  </label>
                  <Select
                    value={step.targeting}
                    onChange={(val) => handleUpdateStep(index, "targeting", val)}
                    style={{ width: "100%" }}
                  >
                    <Option value="all">All Recipients</Option>
                    <Option value="not_opened">Not Opened Previous</Option>
                    <Option value="not_replied">Not Replied</Option>
                    <Option value="opened_not_replied">Opened But Not Replied</Option>
                  </Select>
                </Col>

                <Col xs={12} md={6}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                    <Switch
                      size="small"
                      checked={step.autoFollowUp}
                      onChange={(val) => handleUpdateStep(index, "autoFollowUp", val)}
                    />
                    <Text style={{ fontSize: "12px", color: "#374151" }}>Auto Follow-up</Text>
                  </div>
                </Col>
              </Row>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── Step 3: Sending Config ──────────────────────────────────────────────
  const renderSendingConfig = () => (
    <div>
      <div style={styles.sectionTitle}>
        <span style={{ fontSize: "20px" }}>Drip & Scheduling Settings</span>
      </div>

      <Row gutter={[24, 24]}>
        {/* Batch & Limits */}
        <Col xs={24} md={8}>
          <div style={styles.reviewSection}>
            <label style={{ fontWeight: 600, color: "#374151", fontSize: "13px", marginBottom: "8px", display: "block" }}>
              Batch Size
            </label>
            <InputNumber
              value={dripConfig.batchSize}
              onChange={(val) => handleDripChange("batchSize", val)}
              min={1}
              max={500}
              style={{ width: "100%", ...styles.glassInput }}
            />
            <Text type="secondary" style={{ fontSize: "11px", marginTop: "4px", display: "block" }}>
              Emails sent per batch
            </Text>
          </div>
        </Col>

        <Col xs={24} md={8}>
          <div style={styles.reviewSection}>
            <label style={{ fontWeight: 600, color: "#374151", fontSize: "13px", marginBottom: "8px", display: "block" }}>
              Interval (Minutes)
            </label>
            <InputNumber
              value={dripConfig.intervalMinutes}
              onChange={(val) => handleDripChange("intervalMinutes", val)}
              min={1}
              max={1440}
              style={{ width: "100%", ...styles.glassInput }}
            />
            <Text type="secondary" style={{ fontSize: "11px", marginTop: "4px", display: "block" }}>
              Minutes between batches
            </Text>
          </div>
        </Col>

        <Col xs={24} md={8}>
          <div style={styles.reviewSection}>
            <label style={{ fontWeight: 600, color: "#374151", fontSize: "13px", marginBottom: "8px", display: "block" }}>
              Hourly Limit
            </label>
            <InputNumber
              value={dripConfig.maxPerHour}
              onChange={(val) => handleDripChange("maxPerHour", val)}
              min={1}
              max={2000}
              style={{ width: "100%", ...styles.glassInput }}
            />
            <Text type="secondary" style={{ fontSize: "11px", marginTop: "4px", display: "block" }}>
              Max emails per hour
            </Text>
          </div>
        </Col>

        <Col xs={24} md={8}>
          <div style={styles.reviewSection}>
            <label style={{ fontWeight: 600, color: "#374151", fontSize: "13px", marginBottom: "8px", display: "block" }}>
              Daily Limit
            </label>
            <InputNumber
              value={dripConfig.dailyLimit}
              onChange={(val) => handleDripChange("dailyLimit", val)}
              min={1}
              max={5000}
              style={{ width: "100%", ...styles.glassInput }}
            />
            <Text type="secondary" style={{ fontSize: "11px", marginTop: "4px", display: "block" }}>
              Max emails per day
            </Text>
          </div>
        </Col>

        {/* Send Windows — multiple time slots */}
        <Col xs={24}>
          <div style={styles.reviewSection}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <label style={{ fontWeight: 600, color: "#374151", fontSize: "13px" }}>
                Send Windows
              </label>
              <Button
                size="small"
                type="dashed"
                onClick={() => {
                  const windows = [...(dripConfig.sendWindows || []), { start: "09:00", end: "12:00" }];
                  handleDripChange("sendWindows", windows);
                }}
                style={{ borderRadius: "8px", fontSize: "12px", color: "#6366f1", borderColor: "#6366f1" }}
              >
                + Add Time Slot
              </Button>
            </div>
            <Text type="secondary" style={{ fontSize: "11px", display: "block", marginBottom: "12px" }}>
              Emails will only be sent during these time windows in each recipient's timezone. Add multiple slots to target peak hours (e.g. 7-9 AM + 5-7 PM).
            </Text>
            {(dripConfig.sendWindows || [{ start: "09:00", end: "17:00" }]).map((window, idx) => (
              <Row key={idx} gutter={[12, 8]} align="middle" style={{ marginBottom: "8px" }}>
                <Col xs={1}>
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "#6b7280" }}>{idx + 1}.</span>
                </Col>
                <Col xs={8} md={5}>
                  <Input
                    type="time"
                    value={window.start}
                    onChange={(e) => {
                      const windows = [...(dripConfig.sendWindows || [])];
                      windows[idx] = { ...windows[idx], start: e.target.value };
                      handleDripChange("sendWindows", windows);
                    }}
                    style={{ ...styles.glassInput, height: "38px" }}
                  />
                </Col>
                <Col xs={2} style={{ textAlign: "center" }}>
                  <span style={{ fontSize: "12px", color: "#9ca3af" }}>to</span>
                </Col>
                <Col xs={8} md={5}>
                  <Input
                    type="time"
                    value={window.end}
                    onChange={(e) => {
                      const windows = [...(dripConfig.sendWindows || [])];
                      windows[idx] = { ...windows[idx], end: e.target.value };
                      handleDripChange("sendWindows", windows);
                    }}
                    style={{ ...styles.glassInput, height: "38px" }}
                  />
                </Col>
                <Col xs={5} md={3}>
                  {(dripConfig.sendWindows || []).length > 1 && (
                    <Button
                      size="small"
                      danger
                      type="text"
                      onClick={() => {
                        const windows = (dripConfig.sendWindows || []).filter((_, i) => i !== idx);
                        handleDripChange("sendWindows", windows);
                      }}
                      style={{ fontSize: "12px", borderRadius: "6px" }}
                    >
                      Remove
                    </Button>
                  )}
                </Col>
              </Row>
            ))}

            <div style={{ marginTop: "16px" }}>
              <label style={{ fontSize: "12px", color: "#6b7280", display: "block", marginBottom: "4px" }}>
                Send on these days
              </label>
              <Checkbox.Group
                value={dripConfig.weekdays}
                onChange={(vals) => handleDripChange("weekdays", vals)}
                style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}
              >
                {WEEKDAYS.map((day) => (
                  <Checkbox
                    key={day.value}
                    value={day.value}
                    style={{
                      padding: "4px 10px",
                      borderRadius: "8px",
                      border: "1px solid rgba(200, 200, 220, 0.4)",
                      background: dripConfig.weekdays.includes(day.value)
                        ? "rgba(99, 102, 241, 0.08)"
                        : "transparent",
                      margin: 0,
                    }}
                  >
                    <span style={{ fontSize: "12px", fontWeight: 500 }}>{day.label}</span>
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </div>
          </div>
        </Col>

        {/* Toggles */}
        <Col xs={24} md={12}>
          <div style={styles.reviewSection}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <Text strong style={{ fontSize: "14px", display: "block" }}>Timezone-Aware Sending</Text>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  Adjust send times based on each recipient's timezone
                </Text>
              </div>
              <Switch
                checked={dripConfig.timezoneAware}
                onChange={(val) => handleDripChange("timezoneAware", val)}
              />
            </div>
          </div>
        </Col>

        <Col xs={24} md={12}>
          <div style={styles.reviewSection}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <Text strong style={{ fontSize: "14px", display: "block" }}>Use Best Send Time</Text>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  AI-optimized delivery based on recipient engagement history
                </Text>
              </div>
              <Switch
                checked={dripConfig.useBestTime}
                onChange={(val) => handleDripChange("useBestTime", val)}
              />
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );

  // ── Step 4: Warm-up Config ──────────────────────────────────────────────
  const renderWarmupConfig = () => (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div style={styles.sectionTitle}>
          <span style={{ fontSize: "20px" }}>Email Warm-up Configuration</span>
        </div>
        <Switch
          checked={warmupConfig.enabled}
          onChange={(val) => handleWarmupChange("enabled", val)}
          checkedChildren="Enabled"
          unCheckedChildren="Disabled"
        />
      </div>

      {!warmupConfig.enabled && (
        <Alert
          message="Warm-up is disabled"
          description="Enable warm-up to gradually increase sending volume and protect your sender reputation. Recommended for new email addresses or domains."
          type="info"
          showIcon
          style={{ borderRadius: "12px", marginBottom: "20px" }}
        />
      )}

      <div style={{ opacity: warmupConfig.enabled ? 1 : 0.45, pointerEvents: warmupConfig.enabled ? "auto" : "none" }}>
        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            <div style={styles.reviewSection}>
              <label style={{ fontWeight: 600, color: "#374151", fontSize: "13px", marginBottom: "12px", display: "block" }}>
                Start Volume: <span style={{ color: "#6366f1" }}>{warmupConfig.startVolume}</span> emails/day
              </label>
              <Slider
                value={warmupConfig.startVolume}
                onChange={(val) => handleWarmupChange("startVolume", val)}
                min={1}
                max={100}
                marks={{ 1: "1", 25: "25", 50: "50", 75: "75", 100: "100" }}
                trackStyle={{ background: "linear-gradient(90deg, #6366f1, #8b5cf6)" }}
                handleStyle={{ borderColor: "#6366f1" }}
              />
            </div>
          </Col>

          <Col xs={24} md={12}>
            <div style={styles.reviewSection}>
              <label style={{ fontWeight: 600, color: "#374151", fontSize: "13px", marginBottom: "12px", display: "block" }}>
                Max Volume: <span style={{ color: "#6366f1" }}>{warmupConfig.maxVolume}</span> emails/day
              </label>
              <Slider
                value={warmupConfig.maxVolume}
                onChange={(val) => handleWarmupChange("maxVolume", val)}
                min={10}
                max={1000}
                step={10}
                marks={{ 10: "10", 250: "250", 500: "500", 750: "750", 1000: "1k" }}
                trackStyle={{ background: "linear-gradient(90deg, #6366f1, #8b5cf6)" }}
                handleStyle={{ borderColor: "#6366f1" }}
              />
            </div>
          </Col>

          <Col xs={24} md={12}>
            <div style={styles.reviewSection}>
              <label style={{ fontWeight: 600, color: "#374151", fontSize: "13px", marginBottom: "12px", display: "block" }}>
                Ramp Factor: <span style={{ color: "#6366f1" }}>{warmupConfig.rampFactor}x</span> daily increase
              </label>
              <Slider
                value={warmupConfig.rampFactor}
                onChange={(val) => handleWarmupChange("rampFactor", val)}
                min={1.05}
                max={3.0}
                step={0.05}
                marks={{ 1.05: "1.05x", 1.5: "1.5x", 2.0: "2x", 2.5: "2.5x", 3.0: "3x" }}
                trackStyle={{ background: "linear-gradient(90deg, #6366f1, #8b5cf6)" }}
                handleStyle={{ borderColor: "#6366f1" }}
              />
            </div>
          </Col>

          <Col xs={24} md={12}>
            <div style={styles.reviewSection}>
              <label style={{ fontWeight: 600, color: "#374151", fontSize: "13px", marginBottom: "8px", display: "block" }}>
                Bounce Threshold (%)
              </label>
              <InputNumber
                value={warmupConfig.bounceThreshold}
                onChange={(val) => handleWarmupChange("bounceThreshold", val)}
                min={1}
                max={20}
                style={{ width: "100%", ...styles.glassInput }}
                addonAfter="%"
              />
              <Text type="secondary" style={{ fontSize: "11px", marginTop: "4px", display: "block" }}>
                Pause warm-up if bounce rate exceeds this threshold
              </Text>
            </div>
          </Col>
        </Row>

        {/* Warm-up Preview Chart */}
        <div style={{ marginTop: "20px" }}>
          <div style={styles.sectionTitle}>
            <span style={{ fontSize: "16px" }}>Projected Daily Volume (30 Days)</span>
          </div>
          {WarmUpPreview ? (
            <WarmUpPreview
              projection={warmUpProjection}
              startVolume={warmupConfig.startVolume}
              maxVolume={warmupConfig.maxVolume}
              rampFactor={warmupConfig.rampFactor}
            />
          ) : (
            <div style={styles.reviewSection}>
              {warmUpProjection && warmUpProjection.length > 0 ? (
                <div>
                  <div style={{ display: "flex", gap: "4px", alignItems: "flex-end", height: "120px", padding: "8px 0" }}>
                    {warmUpProjection.slice(0, 30).map((day, idx) => {
                      const maxVal = Math.max(...warmUpProjection.slice(0, 30).map((d) => d.volume || d));
                      const volume = day.volume || day;
                      const heightPct = maxVal > 0 ? (volume / maxVal) * 100 : 0;
                      return (
                        <Tooltip key={idx} title={`Day ${idx + 1}: ${Math.round(volume)} emails`}>
                          <div
                            style={{
                              flex: 1,
                              height: `${Math.max(heightPct, 2)}%`,
                              background: `linear-gradient(180deg, #6366f1, #8b5cf6)`,
                              borderRadius: "3px 3px 0 0",
                              minWidth: "4px",
                              transition: "height 0.3s",
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: "4px" }}>
                    <Text type="secondary" style={{ fontSize: "11px" }}>Day 1</Text>
                    <Text type="secondary" style={{ fontSize: "11px" }}>Day 30</Text>
                  </div>
                </div>
              ) : (
                <FallbackPlaceholder name="WarmUpPreview">
                  <Text type="secondary" style={{ fontSize: "12px", display: "block", marginTop: "8px" }}>
                    Adjust the sliders above to see the projected warm-up volume chart.
                  </Text>
                </FallbackPlaceholder>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ── Step 5: Compliance ──────────────────────────────────────────────────
  const renderCompliance = () => (
    <div>
      <div style={styles.sectionTitle}>
        <span style={{ fontSize: "20px" }}>Compliance & Legal Settings</span>
      </div>

      {ComplianceConfig ? (
        <ComplianceConfig
          compliance={complianceConfig}
          onChange={(val) => setComplianceConfig(val)}
          cooldown={complianceConfig}
          onCooldownChange={(val) => setComplianceConfig(prev => ({ ...prev, ...(typeof val === 'object' ? val : { cooldownDays: val }) }))}
        />
      ) : (
        <Row gutter={[24, 24]}>
          <Col xs={24}>
            <Alert
              message="CAN-SPAM / GDPR Compliance"
              description="Ensure your campaign meets email marketing regulations. All outbound campaigns must include an unsubscribe link and physical mailing address."
              type="warning"
              showIcon
              style={{ borderRadius: "12px", marginBottom: "20px" }}
            />
          </Col>

          <Col xs={24}>
            <div style={styles.reviewSection}>
              <label style={{ fontWeight: 600, color: "#374151", fontSize: "13px", marginBottom: "8px", display: "block" }}>
                Physical Mailing Address
              </label>
              <TextArea
                value={complianceConfig.physicalAddress}
                onChange={(e) => handleComplianceChange("physicalAddress", e.target.value)}
                placeholder="123 Main St, Suite 100, San Francisco, CA 94105"
                rows={2}
                style={{ ...styles.glassInput, resize: "vertical" }}
              />
              <Text type="secondary" style={{ fontSize: "11px", marginTop: "4px", display: "block" }}>
                Required by CAN-SPAM Act. Will appear in email footer.
              </Text>
            </div>
          </Col>

          <Col xs={24} md={12}>
            <div style={styles.reviewSection}>
              <label style={{ fontWeight: 600, color: "#374151", fontSize: "13px", marginBottom: "8px", display: "block" }}>
                Cooldown Period
              </label>
              <InputNumber
                value={complianceConfig.cooldownDays}
                onChange={(val) => handleComplianceChange("cooldownDays", val)}
                min={0}
                max={365}
                style={{ width: "100%", ...styles.glassInput }}
                addonAfter="days"
              />
              <Text type="secondary" style={{ fontSize: "11px", marginTop: "4px", display: "block" }}>
                Minimum days between emails to the same contact across all campaigns
              </Text>
            </div>
          </Col>

          <Col xs={24} md={12}>
            <div style={styles.reviewSection}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <div>
                  <Text strong style={{ fontSize: "14px", display: "block" }}>Consent Required</Text>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Only email contacts who have opted in
                  </Text>
                </div>
                <Switch
                  checked={complianceConfig.consentRequired}
                  onChange={(val) => handleComplianceChange("consentRequired", val)}
                />
              </div>
              <Divider style={{ margin: "12px 0", borderColor: "rgba(200, 200, 220, 0.3)" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <Text strong style={{ fontSize: "14px", display: "block" }}>Include Unsubscribe Link</Text>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Auto-append one-click unsubscribe to footer
                  </Text>
                </div>
                <Switch
                  checked={complianceConfig.includeUnsubscribeLink}
                  onChange={(val) => handleComplianceChange("includeUnsubscribeLink", val)}
                  disabled // always required
                />
              </div>
              <Text type="secondary" style={{ fontSize: "11px", marginTop: "6px", display: "block", color: "#ef4444" }}>
                Unsubscribe link is mandatory and cannot be disabled
              </Text>
            </div>
          </Col>
        </Row>
      )}
    </div>
  );

  // ── Step 6: Verification ────────────────────────────────────────────────
  const renderVerification = () => (
    <div>
      <div style={styles.sectionTitle}>
        <span style={{ fontSize: "20px" }}>Pre-flight Verification</span>
      </div>

      <Alert
        message="Campaign Verification"
        description="We'll run a series of checks to ensure your campaign is ready to send. This process runs in the background."
        type="info"
        showIcon
        style={{ borderRadius: "12px", marginBottom: "24px" }}
      />

      {VerificationPanel ? (
        <VerificationPanel
          campaignId={campaignId}
          sequenceSteps={sequenceSteps}
          selectedContacts={selectedContacts}
          dripConfig={dripConfig}
          warmupConfig={warmupConfig}
          complianceConfig={complianceConfig}
          fromEmail={fromEmail}
          savedPreflightChecks={savedPreflightChecks}
        />
      ) : (
        <div>
          {[
            { name: "Email syntax validation", description: "Check all recipient emails for valid format" },
            { name: "Duplicate detection", description: "Find and flag duplicate email addresses" },
            { name: "Domain verification", description: "Verify sender domain DNS records (SPF, DKIM, DMARC)" },
            { name: "Bounce history check", description: "Check recipients against known bounce list" },
            { name: "Spam score analysis", description: "Run subject lines and templates through spam filters" },
            { name: "Unsubscribe list cross-check", description: "Ensure no unsubscribed contacts are included" },
            { name: "Send limit validation", description: "Verify campaign volume fits within daily/weekly limits" },
            { name: "Template variable check", description: "Ensure all {{variables}} have matching contact data" },
            { name: "Compliance review", description: "Verify CAN-SPAM/GDPR requirements are met" },
          ].map((check, idx) => (
            <div key={idx} style={{ ...styles.stepItemCard, display: "flex", alignItems: "center", gap: "16px" }}>
              <div
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  background: "rgba(99, 102, 241, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Text style={{ color: "#6366f1", fontWeight: 700, fontSize: "12px" }}>{idx + 1}</Text>
              </div>
              <div style={{ flex: 1 }}>
                <Text strong style={{ fontSize: "13px", display: "block" }}>{check.name}</Text>
                <Text type="secondary" style={{ fontSize: "12px" }}>{check.description}</Text>
              </div>
              <Badge
                count="Pending"
                style={{ backgroundColor: "#f3f4f6", color: "#6b7280", fontSize: "11px", borderRadius: "10px", boxShadow: "none" }}
              />
            </div>
          ))}

          {/* Spam Score Card */}
          {SpamScoreCard ? (
            <div style={{ marginTop: "20px" }}>
              <SpamScoreCard spamScore={spamScore} />
            </div>
          ) : spamScore ? (
            <div style={{ ...styles.reviewSection, marginTop: "20px" }}>
              <div style={styles.sectionTitle}>
                <span style={{ fontSize: "16px" }}>Spam Score</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <div
                  style={{
                    width: "64px",
                    height: "64px",
                    borderRadius: "50%",
                    background:
                      (spamScore.score || 0) < 3
                        ? "linear-gradient(135deg, #10b981, #059669)"
                        : (spamScore.score || 0) < 6
                          ? "linear-gradient(135deg, #f59e0b, #d97706)"
                          : "linear-gradient(135deg, #ef4444, #dc2626)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "20px",
                  }}
                >
                  {spamScore.score ?? "--"}
                </div>
                <div>
                  <Text strong style={{ fontSize: "16px", display: "block" }}>
                    {(spamScore.score || 0) < 3 ? "Low Risk" : (spamScore.score || 0) < 6 ? "Medium Risk" : "High Risk"}
                  </Text>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    {spamScore.details || "Run spam check to see details"}
                  </Text>
                </div>
              </div>
            </div>
          ) : null}

          <div style={{ marginTop: "16px", textAlign: "center" }}>
            <Button
              type="primary"
              style={styles.primaryBtn}
              onClick={async () => {
                if (sequenceSteps.length > 0 && sequenceSteps[0].subject) {
                  await dispatch(
                    checkSpamScore({
                      subject: sequenceSteps[0].subject,
                      body: sequenceSteps[0].templateName || "",
                    })
                  );
                } else {
                  message.info("Add a subject line in Step 2 first to check spam score");
                }
              }}
              loading={loading}
            >
              Run Spam Check
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // ── Step 7: Review & Launch ─────────────────────────────────────────────
  const renderReviewAndLaunch = () => (
    <div>
      <div style={styles.sectionTitle}>
        <span style={{ fontSize: "20px" }}>Review & Launch</span>
      </div>

      {/* Campaign Summary */}
      <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
        {(() => {
          const invalidCount = verificationResults?.invalidCount || 0;
          const sendableCount = selectedContacts.length - invalidCount;
          return [
            { label: "Recipients", value: sendableCount > 0 ? sendableCount : selectedContacts.length, color: "#6366f1",
              sub: invalidCount > 0 ? `${invalidCount} excluded` : null },
            { label: "Sequence Steps", value: sequenceSteps.length, color: "#8b5cf6" },
            { label: "Daily Limit", value: dripConfig.dailyLimit, color: "#0ea5e9" },
            { label: "Est. Days", value: Math.ceil((sendableCount > 0 ? sendableCount : selectedContacts.length) / (dripConfig.dailyLimit || 1)), color: "#10b981" },
          ];
        })().map((stat) => (
          <Col xs={12} md={6} key={stat.label}>
            <div style={styles.summaryCard}>
              <div style={{ ...styles.summaryValue, color: stat.color }}>{stat.value}</div>
              <div style={styles.summaryLabel}>{stat.label}</div>
              {stat.sub && (
                <div style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>{stat.sub}</div>
              )}
            </div>
          </Col>
        ))}
      </Row>

      {/* Detail Sections */}
      <Row gutter={[16, 16]}>
        {/* Campaign Info Review */}
        <Col xs={24} md={12}>
          <div style={styles.reviewSection}>
            <div style={{ ...styles.reviewLabel, marginBottom: "12px" }}>Campaign Info</div>
            <div style={{ marginBottom: "8px" }}>
              <Text type="secondary" style={{ fontSize: "11px", display: "block" }}>Name</Text>
              <Text strong style={{ fontSize: "14px" }}>{campaignName || "--"}</Text>
            </div>
            <div style={{ marginBottom: "8px" }}>
              <Text type="secondary" style={{ fontSize: "11px", display: "block" }}>From</Text>
              <Text style={{ fontSize: "13px" }}>{fromEmail || "--"}</Text>
            </div>
            {replyTo && (
              <div style={{ marginBottom: "8px" }}>
                <Text type="secondary" style={{ fontSize: "11px", display: "block" }}>Reply-To</Text>
                <Text style={{ fontSize: "13px" }}>{replyTo}</Text>
              </div>
            )}
            {campaignTags.length > 0 && (
              <div>
                <Text type="secondary" style={{ fontSize: "11px", display: "block", marginBottom: "4px" }}>Tags</Text>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                  {campaignTags.map((tag) => (
                    <Tag key={tag} style={styles.tagChip}>{tag}</Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Col>

        {/* Drip Config Review */}
        <Col xs={24} md={12}>
          <div style={styles.reviewSection}>
            <div style={{ ...styles.reviewLabel, marginBottom: "12px" }}>Sending Schedule</div>
            <div style={{ marginBottom: "8px" }}>
              <Text type="secondary" style={{ fontSize: "11px", display: "block" }}>Batch</Text>
              <Text style={{ fontSize: "13px" }}>
                {dripConfig.batchSize} emails every {dripConfig.intervalMinutes} min
              </Text>
            </div>
            <div style={{ marginBottom: "8px" }}>
              <Text type="secondary" style={{ fontSize: "11px", display: "block" }}>Send Windows</Text>
              {(dripConfig.sendWindows || [{ start: dripConfig.sendWindowStart, end: dripConfig.sendWindowEnd }]).map((w, i) => (
                <Tag key={i} color="geekblue" style={{ marginBottom: "2px" }}>{w.start} - {w.end}</Tag>
              ))}
            </div>
            <div style={{ marginBottom: "8px" }}>
              <Text type="secondary" style={{ fontSize: "11px", display: "block" }}>Days</Text>
              <Text style={{ fontSize: "13px" }}>
                {dripConfig.weekdays.map((d) => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(", ")}
              </Text>
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              {dripConfig.timezoneAware && <Tag color="blue">Timezone-Aware</Tag>}
              {dripConfig.useBestTime && <Tag color="purple">Best Time AI</Tag>}
            </div>
          </div>
        </Col>

        {/* Sequence Steps Review */}
        <Col xs={24}>
          <div style={styles.reviewSection}>
            <div style={{ ...styles.reviewLabel, marginBottom: "12px" }}>Email Sequence</div>
            {sequenceSteps.map((step, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "8px 0",
                  borderBottom: idx < sequenceSteps.length - 1 ? "1px solid rgba(200, 200, 220, 0.25)" : "none",
                }}
              >
                <div
                  style={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "11px",
                    flexShrink: 0,
                  }}
                >
                  {step.stepNumber}
                </div>
                <div style={{ flex: 1 }}>
                  <Text strong style={{ fontSize: "13px" }}>
                    {step.subject || (idx > 0 && sequenceSteps[0]?.subject
                      ? (sequenceSteps[0].subject.startsWith('Re:') ? sequenceSteps[0].subject : `Re: ${sequenceSteps[0].subject}`)
                      : "(No subject)")}
                  </Text>
                  {idx > 0 && (
                    <Text type="secondary" style={{ fontSize: "11px", marginLeft: "8px" }}>
                      +{step.delayDays}d {step.delayHours}h delay
                    </Text>
                  )}
                </div>
                <Tag style={{ fontSize: "11px", borderRadius: "8px" }}>
                  {step.targeting === "all" ? "All" : step.targeting.replace(/_/g, " ")}
                </Tag>
              </div>
            ))}
          </div>
        </Col>

        {/* Warm-up & Compliance Review */}
        <Col xs={24} md={12}>
          <div style={styles.reviewSection}>
            <div style={{ ...styles.reviewLabel, marginBottom: "12px" }}>Warm-up</div>
            {warmupConfig.enabled ? (
              <div>
                <Text style={{ fontSize: "13px", display: "block", marginBottom: "4px" }}>
                  {warmupConfig.startVolume} &rarr; {warmupConfig.maxVolume} emails/day
                </Text>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  Ramp: {warmupConfig.rampFactor}x | Bounce threshold: {warmupConfig.bounceThreshold}%
                </Text>
              </div>
            ) : (
              <Tag color="default">Disabled</Tag>
            )}
          </div>
        </Col>

        <Col xs={24} md={12}>
          <div style={styles.reviewSection}>
            <div style={{ ...styles.reviewLabel, marginBottom: "12px" }}>Compliance</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              <Tag color="green">Unsubscribe Link</Tag>
              {complianceConfig.consentRequired && <Tag color="blue">Consent Required</Tag>}
              <Tag color="default">{complianceConfig.cooldownDays}d Cooldown</Tag>
              {complianceConfig.physicalAddress && <Tag color="green">Address Set</Tag>}
            </div>
          </div>
        </Col>
      </Row>

      <Divider style={{ borderColor: "rgba(200, 200, 220, 0.35)" }} />

      {/* Schedule Picker */}
      <div style={styles.reviewSection}>
        <div style={{ ...styles.reviewLabel, marginBottom: "16px" }}>Launch Options</div>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={12}>
            <div style={{ display: "flex", gap: "12px" }}>
              <Button
                type={launchMode === "now" ? "primary" : "default"}
                onClick={() => setLaunchMode("now")}
                style={
                  launchMode === "now"
                    ? { ...styles.primaryBtn, minWidth: "140px" }
                    : { ...styles.secondaryBtn, minWidth: "140px" }
                }
              >
                Send Now
              </Button>
              <Button
                type={launchMode === "scheduled" ? "primary" : "default"}
                onClick={() => setLaunchMode("scheduled")}
                style={
                  launchMode === "scheduled"
                    ? { ...styles.primaryBtn, minWidth: "140px" }
                    : { ...styles.secondaryBtn, minWidth: "140px" }
                }
              >
                Schedule
              </Button>
            </div>
          </Col>

          {launchMode === "scheduled" && (
            <Col xs={24} md={12}>
              <DatePicker
                showTime={{ format: "HH:mm" }}
                format="YYYY-MM-DD HH:mm"
                value={scheduledAt}
                onChange={setScheduledAt}
                placeholder="Pick date and time"
                style={{ width: "100%", ...styles.glassInput }}
                disabledDate={(current) => current && current.valueOf() < Date.now() - 86400000}
              />
            </Col>
          )}
        </Row>
      </div>

      {/* Save as Playbook */}
      <div style={{ ...styles.reviewSection, marginTop: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Checkbox checked={saveAsPlaybook} onChange={(e) => setSaveAsPlaybook(e.target.checked)}>
            <Text strong style={{ fontSize: "13px" }}>Save as Playbook</Text>
          </Checkbox>
        </div>
        {saveAsPlaybook && (
          <div style={{ marginTop: "12px" }}>
            <Input
              value={playbookName}
              onChange={(e) => setPlaybookName(e.target.value)}
              placeholder="Playbook name (e.g., SaaS Outreach Q2)"
              style={{ ...styles.glassInput, height: "38px", maxWidth: "400px" }}
            />
          </div>
        )}
      </div>

      {/* Launch Buttons */}
      <div style={{ display: "flex", justifyContent: "center", gap: "16px", marginTop: "32px" }}>
        {launchMode === "now" ? (
          <Button
            type="primary"
            size="large"
            style={styles.launchBtn}
            onClick={() => setConfirmLaunchVisible(true)}
            loading={launching}
            disabled={selectedContacts.length === 0}
          >
            Launch Campaign
          </Button>
        ) : (
          <Button
            type="primary"
            size="large"
            style={styles.scheduleBtn}
            onClick={() => {
              if (!scheduledAt) {
                message.warning("Please pick a date and time for scheduling");
                return;
              }
              setConfirmLaunchVisible(true);
            }}
            loading={launching}
            disabled={selectedContacts.length === 0}
          >
            Schedule Campaign
          </Button>
        )}
      </div>

      {/* Confirm Launch Modal */}
      <Modal
        title={
          <span style={{ fontWeight: 600, fontSize: "16px" }}>
            {launchMode === "now" ? "Confirm Launch" : "Confirm Schedule"}
          </span>
        }
        open={confirmLaunchVisible}
        onCancel={() => setConfirmLaunchVisible(false)}
        footer={[
          <Button
            key="cancel"
            onClick={() => setConfirmLaunchVisible(false)}
            style={styles.secondaryBtn}
          >
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={launching}
            onClick={handleLaunch}
            style={launchMode === "now" ? styles.launchBtn : styles.scheduleBtn}
          >
            {launchMode === "now" ? "Yes, Launch Now" : "Yes, Schedule It"}
          </Button>,
        ]}
        width={520}
      >
        <div style={{ padding: "16px 0" }}>
          <Alert
            type="warning"
            showIcon
            message={
              launchMode === "now"
                ? "This will begin sending emails immediately."
                : `This will schedule sending for ${scheduledAt?.format("MMMM D, YYYY [at] h:mm A") || "the selected time"}.`
            }
            description={
              <div style={{ marginTop: "8px" }}>
                <Text>
                  <strong>{selectedContacts.length - (verificationResults?.invalidCount || 0)}</strong> recipients will receive{" "}
                  <strong>{sequenceSteps.length}</strong> email{sequenceSteps.length > 1 ? "s" : ""} in sequence.
                  {(verificationResults?.invalidCount || 0) > 0 && (
                    <span style={{ color: '#ef4444' }}> ({verificationResults.invalidCount} invalid excluded)</span>
                  )}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: "12px", marginTop: "8px", display: "block" }}>
                  Campaign: {campaignName}
                </Text>
              </div>
            }
            style={{ borderRadius: "12px" }}
          />
        </div>
      </Modal>
    </div>
  );

  // ── Step content router ─────────────────────────────────────────────────
  const renderStepContent = () => {
    switch (currentStep) {
      case 0: return renderCampaignInfo();
      case 1: return renderRecipients();
      case 2: return renderBuildSteps();
      case 3: return renderSendingConfig();
      case 4: return renderWarmupConfig();
      case 5: return renderCompliance();
      case 6: return renderVerification();
      case 7: return renderReviewAndLaunch();
      default: return null;
    }
  };

  // ═════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════════════════

  return (
    <div style={styles.wizardContainer}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div>
          <Title level={3} style={{ margin: 0, color: "#1a1a2e", fontWeight: 700 }}>
            {campaignId ? "Edit Campaign" : "New Campaign"}
          </Title>
          <Text type="secondary" style={{ fontSize: "13px" }}>
            {STEP_LABELS[currentStep]} — Step {currentStep + 1} of {STEP_LABELS.length}
          </Text>
        </div>
        <Space>
          <Button
            type="text"
            onClick={handleDiscard}
            style={{ color: "#ef4444", fontWeight: 500 }}
          >
            Discard
          </Button>
          <Button
            type="default"
            onClick={handleSaveDraftAndExit}
            loading={saving}
            style={styles.secondaryBtn}
          >
            Save Draft & Exit
          </Button>
        </Space>
      </div>

      {/* Steps Navigation */}
      <div style={styles.stepsCard}>
        <Steps
          current={currentStep}
          onChange={handleStepClick}
          size="small"
          style={{ maxWidth: "100%" }}
          items={STEP_LABELS.map((label, idx) => ({
            title: (
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: idx === currentStep ? 700 : 500,
                  color: idx <= highestStepReached ? "#4f46e5" : "#9ca3af",
                  cursor: idx <= highestStepReached ? "pointer" : "default",
                }}
              >
                {label}
              </span>
            ),
            description: (
              <span style={{ fontSize: "10px", color: idx <= highestStepReached ? "#6b7280" : "#d1d5db" }}>
                {STEP_DESCRIPTIONS[idx]}
              </span>
            ),
            status: idx < currentStep && idx <= highestStepReached ? "finish" : undefined,
          }))}
        />
      </div>

      {/* Error Banner */}
      {error && (
        <Alert
          message="Error"
          description={typeof error === "string" ? error : "An unexpected error occurred"}
          type="error"
          closable
          showIcon
          style={{ marginBottom: "16px", borderRadius: "12px" }}
        />
      )}

      {/* Step Content */}
      <div style={styles.contentCard}>
        <Spin spinning={loading && saving} tip="Saving...">
          {renderStepContent()}
        </Spin>
      </div>

      {/* Footer Navigation */}
      {currentStep < 7 && (
        <div style={styles.footerBar}>
          <Button
            type="default"
            onClick={handleBack}
            disabled={currentStep === 0}
            style={styles.secondaryBtn}
          >
            Back
          </Button>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {saving && <Text type="secondary" style={{ fontSize: "12px" }}>Saving draft...</Text>}
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {currentStep + 1} / {STEP_LABELS.length}
            </Text>
          </div>

          <Button
            type="primary"
            onClick={handleNext}
            style={styles.primaryBtn}
            loading={saving}
          >
            {currentStep === 6 ? "Review" : "Next"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CampaignWizard;
